import { useId } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import { TERMS_URL } from '@/hooks/useTermsConsent.ts'
import { cn } from '@/utils/cn.ts'

interface TermsCheckboxProps {
  checked: boolean
  onChange: (checked: boolean) => void
  /** Optional id passed to the input — useful for `aria-describedby`. */
  id?: string
  /** Optional class for the wrapping <label>. */
  className?: string
  /**
   * If true, render a small hint reminding the user that the linked CTA is
   * disabled until they accept. Hidden once the box is checked.
   */
  showRequiredHint?: boolean
  /** Disables the checkbox itself (rare — used while the consent is being recorded). */
  disabled?: boolean
}

export function TermsCheckbox({
  checked,
  onChange,
  id,
  className,
  showRequiredHint = false,
  disabled = false,
}: TermsCheckboxProps) {
  const { t } = useTranslation()
  const generatedId = useId()
  const inputId = id ?? generatedId

  return (
    <div className={cn('flex flex-col items-center gap-2', className)}>
      <label
        htmlFor={inputId}
        className="text-fg-secondary flex max-w-[520px] cursor-pointer items-start gap-2 text-left text-[0.8125rem] leading-snug"
      >
        <input
          id={inputId}
          type="checkbox"
          checked={checked}
          disabled={disabled}
          autoComplete="off"
          onChange={(e) => onChange(e.target.checked)}
          data-testid="terms-checkbox"
          className="border-line bg-surface-muted text-accent focus:ring-accent mt-0.5 h-4 w-4 shrink-0 cursor-pointer rounded-sm border focus:ring-1 focus:outline-none disabled:cursor-not-allowed disabled:opacity-50"
        />
        <span>
          <Trans
            i18nKey="terms.checkboxLabel"
            t={t}
            components={{
              a: (
                <a
                  href={TERMS_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-accent underline-offset-2 hover:underline"
                  onClick={(e) => e.stopPropagation()}
                />
              ),
            }}
          />
        </span>
      </label>
      {showRequiredHint && !checked && (
        <p
          role="status"
          data-testid="terms-required-hint"
          className="text-fg-muted text-[0.75rem]"
        >
          {t('terms.requiredHint')}
        </p>
      )}
    </div>
  )
}
